import { useState, useEffect, useCallback, useMemo, Suspense } from "react";
import { Outlet } from "react-router-dom";
import { Grid } from "@mui/material";
import MiniSidebar from "./sidebar";
import SearchAndIcons from "./searchand_icons";
import { ApiService, useApiQuery } from "../utilities";
import { usePermissions } from "../contexts/permissions_context";
import { eNums } from "../constants";
import cookieStorage from "../utilities/cookie_storage";

const ContentFallback = () => (
  <div className="flex items-center justify-center" style={{ flex: 1 }}>
    <div
      className="w-8 h-8 rounded-full border-4 border-primary-blue animate-spin"
      style={{ borderTopColor: "transparent" }}
    />
  </div>
);

function AppShell() {
  const userType = cookieStorage.getItem("userType");
  const { setRolePermissions } = usePermissions();

  // Breadcrumb paths and other per-page navbar props are lifted here
  // so SearchAndIcons stays mounted and never flickers on navigation.
  const [layoutProps, setLayoutPropsState] = useState({});

  const { data: permissionsData, isLoading } = useApiQuery(
    ["rolePermissions"],
    ApiService.getRolePermissionsById,
    {},
    { enabled: userType === eNums?.AGENT }
  );

  useEffect(() => {
    if (permissionsData?._id) {
      setRolePermissions(permissionsData);
    }
  }, [permissionsData]);

  // Stable setter passed down via outlet context so NewLayout can push paths up.
  // Guards with JSON comparison so calling setLayoutProps on every render from
  // NewLayout doesn't cause a state update (and parent re-render) when unchanged.
  const setLayoutProps = useCallback((newProps) => {
    setLayoutPropsState((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(newProps)) return prev;
      return newProps;
    });
  }, []);

  const outletContext = useMemo(
    () => ({ setLayoutProps }),
    [setLayoutProps]
  );

  return (
    <Grid container className="h-full">
      <div className="flex w-full">
        {/* Sidebar — always mounted, never inside Suspense */}
        <MiniSidebar loading={isLoading} />

        <div className="flex flex-col main-right-content">
          {/* Navbar — always mounted, never inside Suspense */}
          <SearchAndIcons userType={userType} props={layoutProps} />

          {/* Only the page content area suspends on lazy-route transitions */}
          <Suspense fallback={<ContentFallback />}>
            <Outlet context={outletContext} />
          </Suspense>
        </div>
      </div>
    </Grid>
  );
}

export default AppShell;
